import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import axios from 'axios'
import { resolveLanguageCodes, parseDefaultLanguageCode } from '../constants/preferredLanguages'
import { isSessionTimeoutError, emitSessionTimeout, clearSessionExpiredFlag, isAuthFailureError, isTransientAuthError } from '../utils/sessionTimeout'

const LAST_ACTIVITY_KEY = 'rs_last_activity'

const AuthContext = createContext()

function parsePreferredLanguages(user) {
  if (!user) return ['en']
  if (Array.isArray(user.preferred_languages) && user.preferred_languages.length) {
    return resolveLanguageCodes(user.preferred_languages, 3)
  }
  try {
    const meta = JSON.parse(user.registration_notes || '{}')
    if (meta?.languages?.length) return resolveLanguageCodes(meta.languages, 3)
  } catch {
    /* ignore */
  }
  return ['en']
}

function hasStoredToken() {
  return typeof window !== 'undefined' && !!localStorage.getItem('token')
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recovering, setRecovering] = useState(false)
  const userRef = useRef(null)

  const setUserState = useCallback((next) => {
    userRef.current = next
    setUser(next)
  }, [])

  const fetchUser = useCallback(async (attempt = 0) => {
    if (!hasStoredToken()) {
      setRecovering(false)
      return null
    }
    try {
      const res = await axios.get('/api/auth/me')
      setUserState(res.data)
      clearSessionExpiredFlag()
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      setRecovering(false)
      return res.data
    } catch (err) {
      if (isSessionTimeoutError(err)) {
        emitSessionTimeout()
        setRecovering(false)
        return null
      }
      if (isAuthFailureError(err)) {
        setUserState(null)
        localStorage.removeItem('token')
        sessionStorage.removeItem(LAST_ACTIVITY_KEY)
        clearSessionExpiredFlag()
        delete axios.defaults.headers.common['Authorization']
        setRecovering(false)
        return null
      }
      if (isTransientAuthError(err) && attempt < 3) {
        setRecovering(true)
        await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)))
        return fetchUser(attempt + 1)
      }
      if (userRef.current) {
        setRecovering(false)
        return userRef.current
      }
      setRecovering(true)
      return null
    }
  }, [setUserState])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      fetchUser().finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [fetchUser])

  const login = async (email, password, extras = {}) => {
    const res = await axios.post('/api/auth/login', { email, password, ...extras })
    const { token, user: loggedInUser } = res.data
    localStorage.setItem('token', token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
    clearSessionExpiredFlag()
    setUserState(loggedInUser)
    setRecovering(false)
    return loggedInUser
  }

  const logout = () => {
    localStorage.removeItem('token')
    sessionStorage.removeItem(LAST_ACTIVITY_KEY)
    clearSessionExpiredFlag()
    delete axios.defaults.headers.common['Authorization']
    setUserState(null)
    setRecovering(false)
  }

  const isPaid = !!(user?.is_paid || user?.company_paid)
  const isCompanyPaid = !!user?.company_paid
  const role = user?.role || ''
  const isAdmin = role === 'Admin'
  const preferredLanguages = useMemo(() => parsePreferredLanguages(user), [user])
  const defaultLanguage = useMemo(() => parseDefaultLanguageCode(user), [user])

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        recovering,
        isPaid,
        isCompanyPaid,
        preferredLanguages,
        defaultLanguage,
        role,
        isAdmin,
        refreshUser: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
